import * as React from 'react';

import { useId } from '@coursera/cds-core';

import type { Placement } from 'bundles/common/components/Tooltip';
import { FloatingTooltip } from 'bundles/common/components/Tooltip';

import 'css!./__styles__/ProgressTrackerPopover';

type ProgressTrackerPopoverProps = {
  popoverContent: React.ReactNode;
  placement?: Placement;
  defaultOverlayShown?: boolean;
  children: React.ReactNode;
};

const ProgressTrackerPopover = (props: ProgressTrackerPopoverProps) => {
  const { children, placement = 'top', popoverContent, ...rest } = props;
  const [isOpen, setIsOpen] = React.useState(false);
  const id = useId();
  const tooltipId = useId();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  const handleMouseEnter = () => {
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    setIsOpen(false);
  };

  const tooltip = (
    <div role="tooltip" id={tooltipId} aria-hidden={!isOpen}>
      {popoverContent}
    </div>
  );

  const wrappedChildren = (
    <g
      role="button"
      tabIndex={0}
      id={id}
      aria-expanded={isOpen}
      // Use aria-labelledby instead of aria-describedby to ensure content is read
      aria-labelledby={isOpen ? tooltipId : undefined}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={() => setIsOpen(!isOpen)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {React.Children.only(children)}
    </g>
  );

  return (
    <FloatingTooltip
      placement={placement}
      message={tooltip}
      variant="secondary"
      show={isOpen}
      {...rest}
      id="rc-ProgressTrackerPopover-Popover"
      className="progressTrackerPopover"
    >
      {wrappedChildren}
    </FloatingTooltip>
  );
};

export default ProgressTrackerPopover;
